import { type CSSProperties, useEffect, useState } from 'react';
import {
  type KyoenMove,
  type KyoenPoint,
  type KyoenState,
  legalKey,
  outcomeGeometry,
  pointLabel,
  type Side,
} from '../../games/kyoen';
import { usePendingConfirm } from '../../hooks/usePendingConfirm';
import { BoardCoords } from './BoardCoords';
import './KyoenBoard.css';

interface Props {
  state: KyoenState;
  legal?: KyoenMove[];
  yourSide?: Side | null;
  myTurnAllowed?: boolean;
  onMove?: (move: KyoenMove) => void;
  mini?: boolean;
  lastMove?: KyoenMove;
  staticView?: boolean;
  suppressTurnStatus?: boolean;
}

const sideLabel = (side: Side) => (side === 'First' ? '先手' : '後手');

/** 置石は2タップ。石の選択と明示的な宣言を分離し、勝敗はエンジンが検証する。 */
export function KyoenBoard({
  state,
  legal,
  yourSide,
  myTurnAllowed,
  onMove,
  mini = false,
  lastMove,
  staticView = false,
}: Props) {
  const n = state.size;
  const isMyTurn = myTurnAllowed ?? (yourSide != null && yourSide === state.side);
  const canPlay = isMyTurn && !state.outcome && !mini && !staticView && onMove != null;
  const canClaim = canPlay;
  const legalSet = new Set((legal ?? []).flatMap((move) => 'Place' in move ? [legalKey(move.Place.row, move.Place.col)] : []));
  const stones = new Map(state.stones.map((point, i) => [legalKey(point.row, point.col), i + 1]));
  const endPoints = new Set((state.outcome?.circle ?? []).map((p) => legalKey(p.row, p.col)));
  const geometry = outcomeGeometry(state.outcome);
  const lastPoint = lastMove && 'Place' in lastMove ? lastMove.Place : state.stones[state.stones.length - 1];
  const lastKey = lastPoint ? legalKey(lastPoint.row, lastPoint.col) : null;
  const { pending, tap, clear } = usePendingConfirm<string>({ state, canPlay });
  const [claimPoints, setClaimPoints] = useState<KyoenPoint[]>([]);
  const stateKey = JSON.stringify(state);
  // Changing another board alone must not clear this board's selected stones.
  // biome-ignore lint/correctness/useExhaustiveDependencies: stateKey compares serialized board content, not its reference.
  useEffect(() => { setClaimPoints([]); }, [stateKey, canPlay]);
  const visibleClaim = canClaim ? claimPoints : [];
  const claimKeys = new Set(visibleClaim.map((p) => legalKey(p.row, p.col)));
  // A parent's turn gate can change without changing this board state in a multi-board match.
  const visiblePending = canPlay ? pending : null;
  const showOppHint = !mini && !staticView && !isMyTurn && !state.outcome;
  const outcomeText = state.outcome
    ? state.outcome.winner
      ? `${state.outcome.reason === 'InvalidClaim' ? '誤宣言' : state.outcome.reason === 'Collinear' ? '直線を発見' : '共円を発見'} — ${sideLabel(state.outcome.winner)}の勝ち`
      : '引き分け'
    : null;

  const selectStone = (point: KyoenPoint) => {
    clear();
    const key = legalKey(point.row, point.col);
    const next = claimKeys.has(key)
      ? visibleClaim.filter((p) => legalKey(p.row, p.col) !== key)
      : [...visibleClaim, point];
    setClaimPoints(next);
  };

  return (
    <div className={`kyoen-frame${mini ? ' mini' : ''}`} data-testid="kyoen-frame">
      {!mini && outcomeText && <div className="kyoen-outcome" role="status" data-testid="kyoen-outcome">{outcomeText}</div>}
      <div
        className={`kyoen-board${mini ? ' mini' : ''}${showOppHint ? ' not-my-turn' : ''}`}
        style={{ '--kyoen-intervals': n - 1 } as CSSProperties}
        role="group"
        aria-label={`共円 ${n}×${n}の盤`}
        data-testid="kyoen-board"
        onKeyDown={(event) => {
          if (event.key === 'Escape') { clear(); setClaimPoints([]); }
        }}
        onClick={(event) => {
          if (!(event.target as HTMLElement).closest('.kyoen-point')) { clear(); setClaimPoints([]); }
        }}
      >
        <div className="kyoen-grid-area">
          {!mini && (
            <BoardCoords
              variant="intersection"
              files={Array.from({ length: n }, (_, col) => String.fromCharCode(65 + col))}
              ranks={Array.from({ length: n }, (_, row) => `${n - row}`)}
            />
          )}
          <svg className="kyoen-grid-svg" viewBox={`0 0 ${n - 1} ${n - 1}`} aria-hidden="true">
            {Array.from({ length: n }, (_, i) => (
              <g key={i}>
                <line x1={0} y1={i} x2={n - 1} y2={i} />
                <line x1={i} y1={0} x2={i} y2={n - 1} />
              </g>
            ))}
          </svg>
          {geometry && (
            <svg className="kyoen-ending-svg" viewBox={`0 0 ${n - 1} ${n - 1}`} aria-hidden="true">
              {geometry.kind === 'circle' ? (
                <circle
                  cx={geometry.cx}
                  cy={geometry.cy}
                  r={geometry.radius}
                  data-testid="kyoen-outcome-circle"
                />
              ) : (
                <line
                  x1={geometry.x1}
                  y1={geometry.y1}
                  x2={geometry.x2}
                  y2={geometry.y2}
                  data-testid="kyoen-outcome-line"
                />
              )}
            </svg>
          )}
          {Array.from({ length: n * n }, (_, i) => {
            const row = Math.floor(i / n);
            const col = i % n;
            const key = legalKey(row, col);
            const order = stones.get(key);
            const isLegal = canPlay && legalSet.has(key) && order == null;
            const selectableStone = canClaim && order != null;
            const isPending = visiblePending === key;
            const isClaimSelected = claimKeys.has(key);
            const isEnding = endPoints.has(key);
            const label = pointLabel({ row, col }, n);
            return (
              <button
                type="button"
                key={key}
                className={`kyoen-point${order ? ' occupied' : ''}${isLegal ? ' legal' : ''}${selectableStone ? ' claimable' : ''}${isClaimSelected ? ' claim-selected' : ''}${isPending ? ' pending' : ''}${lastKey === key ? ' last-move' : ''}${isEnding ? ' ending-point' : ''}`}
                style={{ left: `${(col / (n - 1)) * 100}%`, top: `${(row / (n - 1)) * 100}%` }}
                disabled={!isLegal && !selectableStone}
                onClick={() => {
                  if (selectableStone) selectStone({ row, col });
                  else { setClaimPoints([]); tap(key, isLegal, () => onMove?.({ Place: { row, col } })); }
                }}
                aria-label={`${label}${order ? ` 第${order}手の石` : ' 空点'}${lastKey === key ? ' 直前の石' : ''}${isEnding ? ' 終局の4点' : ''}`}
                aria-pressed={isPending || isClaimSelected}
                data-testid={`kyoen-point-${row}-${col}`}
                data-occupied={order != null}
                data-ending={isEnding}
                data-claim-selected={isClaimSelected}
                data-last-stone={lastKey === key && order != null}
              >
                {order != null && <span className="kyoen-stone" aria-hidden="true" />}
                {isPending && <span className="kyoen-pending-stone" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
        {showOppHint && <div className="board-turn-hint" aria-hidden="true">手番待ち</div>}
      </div>
      {canClaim && <div className="kyoen-action"><button type="button" className="kyoen-declare" data-testid="kyoen-declare" onClick={() => onMove?.({ Claim: { points: visibleClaim } })}>最後の石を含む4石の円を指摘</button></div>}

    </div>
  );
}
